import React from 'react';
import CertificationsSection from '../components/CertificationsSection';
import { FaMapMarkerAlt, FaCity, FaChartLine, FaHandshake, FaLeaf, FaGlobe } from 'react-icons/fa';
import { motion } from 'framer-motion';

const About = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Header Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-primary-900 text-white py-20 lg:py-28 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl"></div>

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="inline-block mb-4" variants={fadeInUp}>
            <span className="bg-secondary-500 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
              Who We Are
            </span>
          </motion.div>
          <motion.h1 className="text-4xl lg:text-6xl font-bold mb-6" variants={fadeInUp}>
            About <span className="text-secondary-500">Trends Dynamiques</span>
          </motion.h1>
          <motion.p className="text-lg lg:text-xl leading-relaxed max-w-4xl mx-auto opacity-90" variants={fadeInUp}>
            Your Trusted Partner in Strategy, Growth, and Impact.
          </motion.p>

          {/* Stats */}
          <motion.div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mt-12" variants={fadeInUp}>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">50+</div>
              <div className="text-sm text-gray-300">Years Combined Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">50+</div>
              <div className="text-sm text-gray-300">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">2</div>
              <div className="text-sm text-gray-300">Office Locations</div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* About Description */}
      <section className="py-16 lg:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg lg:text-xl leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
            Trends Dynamiques Consulting Limited is a Tanzanian-owned management consulting firm with offices in Zanzibar and Dar es Salaam, bringing over 50 years of combined experience in delivering high-quality solutions for businesses, non-profits, investors, governments, and development partners.
          </p>
          <p className="text-lg lg:text-xl leading-relaxed text-gray-700 dark:text-gray-300">
            We specialize in providing tailored advisory and consulting services across diverse industries, helping our clients make informed decisions, achieve sustainable growth, and demonstrate measurable impact.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-navy-900 via-navy-800 to-primary-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-80 h-80 bg-secondary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"></div>
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="text-center mb-14" variants={fadeInUp}>
            <span className="bg-secondary-500 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
              What We Do
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold mt-6 mb-4">
              How We Drive <span className="text-secondary-500">Results</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              We combine deep local expertise with proven global methodologies to deliver
              consulting solutions that make a real difference for our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: FaChartLine,
                title: "Strategic Advisory",
                description: "We help organizations define clear strategies, set achievable goals, and build the roadmaps needed to reach them — from market entry to organizational transformation.",
                accent: "from-blue-500 to-blue-700"
              },
              {
                icon: FaHandshake,
                title: "Business Development",
                description: "We connect businesses with opportunities — identifying partners, facilitating investment, and opening doors across Tanzania and the broader East African market.",
                accent: "from-secondary-500 to-secondary-700"
              },
              {
                icon: FaLeaf,
                title: "Sustainable Growth",
                description: "We design programs and frameworks that balance economic performance with social and environmental responsibility, ensuring our clients grow with lasting impact.",
                accent: "from-green-500 to-green-700"
              },
              {
                icon: FaGlobe,
                title: "Development Consulting",
                description: "We partner with governments, NGOs, and development agencies to design, implement, and evaluate programs that deliver measurable outcomes for communities.",
                accent: "from-purple-500 to-purple-700"
              }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/10 hover:border-secondary-500/50 group"
                >
                  <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${item.accent} rounded-xl shadow-lg mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Locations */}
      <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 dark:text-white mb-3">
              Our Strategic Locations
            </h2>
            <div className="w-24 h-1 bg-secondary-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {[
              {
                icon: FaMapMarkerAlt,
                city: "Zanzibar Office",
                address: "P.O. BOX 229, ZANZIBAR",
                description: "Our main office located in the heart of Stone Town, serving clients across the archipelago and providing specialized tourism and investment advisory services."
              },
              {
                icon: FaCity,
                city: "Dar es Salaam Office",
                address: "Commercial District",
                description: "Our mainland office serving the commercial capital, focusing on business development, government partnerships, and large-scale consulting projects."
              }
            ].map((location, index) => {
              const IconComponent = location.icon;
              return (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-navy-900 rounded-xl shadow-lg mr-4">
                      <IconComponent className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-navy-900 dark:text-white">{location.city}</h3>
                      <p className="text-sm text-secondary-500 font-medium">{location.address}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {location.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      {/* <section className="py-16 lg:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 dark:text-white mb-3">
              Our Core Values
            </h2>
            <div className="w-24 h-1 bg-secondary-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FaAward,
                title: "Excellence",
                description: "We deliver high-quality solutions that exceed expectations and create lasting value for our clients."
              },
              {
                icon: FaShieldAlt,
                title: "Integrity",
                description: "We conduct business with the highest ethical standards and transparency in all our engagements."
              },
              {
                icon: FaLightbulb,
                title: "Innovation",
                description: "We embrace creative solutions and cutting-edge approaches to solve complex challenges."
              },
              {
                icon: FaHeart,
                title: "Impact",
                description: "We focus on creating sustainable, measurable outcomes that contribute to long-term success."
              }
            ].map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-navy-900 rounded-xl shadow-lg mb-4">
                    <IconComponent className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-navy-900 dark:text-white">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* Certifications & Legal Compliance Section */}
      <CertificationsSection />
    </div>
  );
};

export default About;